import { useState } from "react";
import { ArrowUpRight, Search, MapPin, Users, Sparkles } from "lucide-react";
import Reveal from "./Reveal";

const BOOKING_URL = "https://booking.tourism-connect.com";

const METRICS = [
  ["1,200+", "Vetted properties"],
  ["450+", "Verified operators"],
  ["24/7", "Live availability"],
];

export default function Spotlight() {
  const [destination, setDestination] = useState("Botswana");
  const [experience, setExperience] = useState("Safari");
  const [guests, setGuests] = useState(2);

  const launch = () => {
    window.open(BOOKING_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="booking" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <Reveal>
          <p className="kicker mb-6">Live Subdomain</p>
          <h2 className="font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl lg:text-5xl">
            The booking engine is{" "}
            <span className="text-gold">already live.</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-muted sm:text-lg">
            Real inventory, real operators, real confirmations.{" "}
            <span className="font-mono text-sm text-cream">booking.tourism-connect.com</span>{" "}
            connects traveller demand directly to Southern Africa's finest lodges and
            experiences — no middlemen, no delays.
          </p>

          <div className="mt-10 flex flex-wrap gap-x-10 gap-y-6">
            {METRICS.map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-3xl text-cream">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">{l}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <button data-testid="subdomain-launch-booking-button" onClick={launch} className="btn-gold">
              Open booking.tourism-connect.com <ArrowUpRight size={16} />
            </button>
            <span
              data-testid="subdomain-live-badge"
              className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-green-500"
            >
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-green-500" />
              Live online
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative rounded-2xl border border-goldline bg-panel/80 p-6 shadow-[var(--shadow-card)] backdrop-blur-xl sm:p-8">
            <div className="absolute -top-3 left-6 rounded-full border border-gold/40 bg-ink px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">
              Search simulator
            </div>
            <p className="font-serif text-2xl text-cream">Plan it. Book it. Live.</p>
            <p className="mt-1 text-sm text-subtle">A taste of the engine — searches route to live inventory.</p>

            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                  <MapPin size={11} /> Destination
                </span>
                <select
                  data-testid="simulator-destination-select"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="field"
                >
                  {["Botswana", "Namibia", "South Africa", "Zimbabwe"].map((d) => (
                    <option key={d} className="bg-panel">{d}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                  <Sparkles size={11} /> Experience
                </span>
                <select
                  data-testid="simulator-experience-select"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  className="field"
                >
                  {["Safari", "Luxury Lodge", "Adventure", "Culture & Heritage"].map((x) => (
                    <option key={x} className="bg-panel">{x}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                  <Users size={11} /> Travellers
                </span>
                <input
                  data-testid="simulator-guests-input"
                  type="number"
                  min="1"
                  max="20"
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  className="field"
                />
              </label>

              <button data-testid="simulator-search-button" onClick={launch} className="btn-gold w-full justify-center">
                <Search size={15} /> Search live inventory
              </button>
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                Routes to the live booking engine
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
